import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import Helpers from "../Config/Helpers";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  TextField,
  Stack,
  Breadcrumbs,
  Link as MuiLink,
  Grid,
  Chip,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import SellIcon from "@mui/icons-material/Sell";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import PrintIcon from "@mui/icons-material/Print";
import TodayIcon from "@mui/icons-material/Today";
import ReusableDataGrid from "../Components/ReusableDataGrid";

const toLocalDateInput = (value = new Date()) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getMonthBounds = (value = new Date()) => {
  const date = new Date(value);
  return {
    start: toLocalDateInput(new Date(date.getFullYear(), date.getMonth(), 1)),
    end: toLocalDateInput(new Date(date.getFullYear(), date.getMonth() + 1, 0)),
  };
};

const formatCurrency = (value) =>
  `$${Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

const formatDateInputLabel = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).split("-");
  if (!year || !month || !day) return formatDate(value);
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString();
};

function PartReports() {
  const navigate = useNavigate();
  const initialRange = getMonthBounds();
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [report, setReport] = useState({
    summary: {},
    vendors: [],
    parts: [],
  });
  const [loading, setLoading] = useState(true);

  const getReport = useCallback(() => {
    setLoading(true);
    const tzOffsetMinutes = new Date().getTimezoneOffset();
    const query = `?start=${encodeURIComponent(startDate)}&end=${encodeURIComponent(
      endDate,
    )}&tzOffsetMinutes=${tzOffsetMinutes}`;

    axios
      .get(
        `${Helpers.baseUrl}/reports/parts/${Helpers.authParentId}${query}`,
        Helpers.headers,
      )
      .then((response) => {
        setReport({
          summary: response.data?.summary || {},
          vendors: Array.isArray(response.data?.vendors) ? response.data.vendors : [],
          parts: Array.isArray(response.data?.parts) ? response.data.parts : [],
        });
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [endDate, navigate, startDate]);

  useEffect(() => {
    getReport();
  }, [getReport]);

  const summary = useMemo(() => report.summary || {}, [report.summary]);
  const selectedRangeLabel = useMemo(
    () =>
      startDate === endDate
        ? formatDateInputLabel(startDate)
        : `${formatDateInputLabel(startDate)} - ${formatDateInputLabel(endDate)}`,
    [endDate, startDate],
  );

  const setTodayRange = () => {
    const today = toLocalDateInput();
    setStartDate(today);
    setEndDate(today);
  };

  const printSelectedRange = () => {
    window.print();
  };

  const statCards = useMemo(
    () => [
      {
        title: "Purchased",
        value: `${Number(summary.purchasedCount || 0)} parts`,
        secondary: formatCurrency(summary.purchasedCost),
        icon: <ShoppingCartIcon sx={{ fontSize: 34 }} />,
        color: "#2563eb",
        bg: "rgba(37, 99, 235, 0.10)",
      },
      {
        title: "Sold",
        value: `${Number(summary.soldCount || 0)} parts`,
        secondary: formatCurrency(summary.soldRevenue),
        icon: <SellIcon sx={{ fontSize: 34 }} />,
        color: "#059669",
        bg: "rgba(5, 150, 105, 0.10)",
      },
      {
        title: "Returned",
        value: `${Number(summary.returnedCount || 0)} parts`,
        secondary: formatCurrency(summary.refundAmount),
        icon: <AssignmentReturnIcon sx={{ fontSize: 34 }} />,
        color: "#dc2626",
        bg: "rgba(220, 38, 38, 0.10)",
      },
      {
        title: "Open",
        value: `${Number(summary.openCount || 0)} parts`,
        secondary: formatCurrency(summary.openCost),
        icon: <PendingActionsIcon sx={{ fontSize: 34 }} />,
        color: "#d97706",
        bg: "rgba(217, 119, 6, 0.10)",
      },
    ],
    [summary],
  );

  const vendorColumns = [
    { field: "vendor", headerName: "Vendor", flex: 1, minWidth: 180 },
    {
      field: "purchasedCount",
      headerName: "Bought Qty",
      width: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "purchasedCost",
      headerName: "Bought $",
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "soldCount",
      headerName: "Sold Qty",
      width: 110,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "soldRevenue",
      headerName: "Sold $",
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "returnCount",
      headerName: "Returned Qty",
      width: 125,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "refundAmount",
      headerName: "Refund $",
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "openCount",
      headerName: "Open Qty",
      width: 105,
      align: "center",
      headerAlign: "center",
    },
  ];

  const partColumns = [
    { field: "name", headerName: "Part", flex: 1, minWidth: 180 },
    { field: "partNumber", headerName: "Part #", width: 120 },
    { field: "vendor", headerName: "Vendor", width: 140 },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: (params) => {
        const paletteByStatus = {
          Available: { bg: "#dcfce7", color: "#166534" },
          Assigned: { bg: "#dbeafe", color: "#1d4ed8" },
          Sold: { bg: "#ede9fe", color: "#6d28d9" },
          Returned: { bg: "#fee2e2", color: "#b91c1c" },
          "Needs Review": { bg: "#fef3c7", color: "#92400e" },
        };
        const palette = paletteByStatus[params.value] || {
          bg: "#e5e7eb",
          color: "#374151",
        };
        return (
          <Chip
            label={params.value || "-"}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: palette.bg,
              color: palette.color,
            }}
          />
        );
      },
    },
    {
      field: "quantity",
      headerName: "Qty",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "buyPrice",
      headerName: "Buy",
      width: 110,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "sellPrice",
      headerName: "Sell",
      width: 110,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: "purchaseDate",
      headerName: "Purchased",
      width: 110,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "resolvedAt",
      headerName: "Resolved",
      width: 110,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: "linkedInvoice",
      headerName: "Invoice",
      width: 100,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "customer",
      headerName: "Customer",
      minWidth: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "refundAmount",
      headerName: "Refund",
      width: 110,
      renderCell: (params) => formatCurrency(params.value),
    },
  ];

  return (
    <>
      <Box
        className="screen-only"
        sx={{
          pt: { xs: 10, sm: 11, md: 12 },
          pb: 3,
          minHeight: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: "100% !important",
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              p: 3,
              background:
                "linear-gradient(135deg, #0f766e 0%, #0891b2 55%, #2563eb 100%)",
              borderRadius: 2,
              color: "white",
            }}
          >
            <Breadcrumbs
              separator={
                <NavigateNextIcon
                  fontSize="small"
                  sx={{ color: "rgba(255,255,255,0.7)" }}
                />
              }
              sx={{ mb: 2 }}
            >
              <MuiLink
                component={Link}
                to="/user/dashboard"
                underline="hover"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
                Home
              </MuiLink>
              <MuiLink
                component={Link}
                to="/user/reports"
                underline="hover"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  color: "rgba(255,255,255,0.9)",
                }}
              >
                Reports
              </MuiLink>
              <Typography sx={{ color: "white", fontWeight: 600 }}>
                Parts Report
              </Typography>
            </Breadcrumbs>

            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Inventory2Icon sx={{ fontSize: 38 }} />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 700 }}>
                    Parts Report
                  </Typography>
                  <Typography sx={{ color: "rgba(255,255,255,0.88)" }}>
                    Track special-order purchases, sales, returns, and open parts by vendor.
                  </Typography>
                </Box>
              </Stack>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={printSelectedRange}
                disabled={loading}
                sx={{
                  color: "white",
                  borderColor: "rgba(255,255,255,0.45)",
                  textTransform: "none",
                }}
              >
                Print Selected Dates
              </Button>
            </Stack>
          </Paper>

          <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 3 }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(event) => {
                  const value = event.target.value;
                  setStartDate(value);
                  if (value > endDate) {
                    setEndDate(value);
                  }
                }}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="contained"
                onClick={getReport}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Refresh Report
              </Button>
              <Button
                variant="outlined"
                startIcon={<TodayIcon />}
                onClick={setTodayRange}
                sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              >
                Today
              </Button>
              <Typography variant="body2" sx={{ color: "#64748b", ml: { md: "auto" } }}>
                Showing {selectedRangeLabel}
              </Typography>
            </Stack>
          </Paper>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {statCards.map((card) => (
              <Grid item xs={12} sm={6} lg={3} key={card.title}>
                <Card elevation={0} sx={{ borderRadius: 2, height: "100%" }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Box>
                        <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
                          {card.title}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
                          {card.value}
                        </Typography>
                        <Typography variant="body2" sx={{ color: card.color, fontWeight: 600 }}>
                          {card.secondary}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: card.color,
                          backgroundColor: card.bg,
                        }}
                      >
                        {card.icon}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Paper elevation={1} sx={{ borderRadius: 2, p: 2, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Vendor Summary
            </Typography>
            <ReusableDataGrid
              rows={report.vendors}
              loading={loading}
              columns={vendorColumns}
              pageSize={10}
              pageSizeOptions={[10, 25, 50]}
              autoHeight
              noRowsMessage="No vendor activity in this date range"
            />
          </Paper>

          <Paper elevation={1} sx={{ borderRadius: 2, p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
              Part Activity
            </Typography>
            <ReusableDataGrid
              rows={report.parts}
              loading={loading}
              columns={partColumns}
              pageSize={10}
              pageSizeOptions={[10, 25, 50, 100]}
              autoHeight
              noRowsMessage="No special-order parts found in this date range"
            />
          </Paper>
        </Container>
      </Box>
      <Box className="print-only" sx={{ display: "none" }}>
        <div className="parts-print-report">
          <div className="parts-print-header">
            <div>
              <h1>Parts Report</h1>
              <p>{Helpers.authShop?.name || "Inventory System"}</p>
            </div>
            <div className="parts-print-meta">
              <div>
                <strong>Date Range:</strong> {selectedRangeLabel}
              </div>
              <div>
                <strong>Generated:</strong> {new Date().toLocaleString()}
              </div>
            </div>
          </div>

          <div className="parts-print-summary">
            <div>
              <span>Purchased</span>
              <strong>{Number(summary.purchasedCount || 0)} parts</strong>
              <em>{formatCurrency(summary.purchasedCost)}</em>
            </div>
            <div>
              <span>Sold</span>
              <strong>{Number(summary.soldCount || 0)} parts</strong>
              <em>{formatCurrency(summary.soldRevenue)}</em>
            </div>
            <div>
              <span>Returned</span>
              <strong>{Number(summary.returnedCount || 0)} parts</strong>
              <em>{formatCurrency(summary.refundAmount)}</em>
            </div>
            <div>
              <span>Open</span>
              <strong>{Number(summary.openCount || 0)} parts</strong>
              <em>{formatCurrency(summary.openCost)}</em>
            </div>
          </div>

          <section className="parts-print-section">
            <h2>Vendor Summary</h2>
            <table>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th>Bought Qty</th>
                  <th>Bought $</th>
                  <th>Sold Qty</th>
                  <th>Sold $</th>
                  <th>Returned Qty</th>
                  <th>Refund $</th>
                  <th>Open Qty</th>
                </tr>
              </thead>
              <tbody>
                {report.vendors.length > 0 ? (
                  report.vendors.map((vendor) => (
                    <tr key={vendor.id || vendor.vendor}>
                      <td>{vendor.vendor || "-"}</td>
                      <td>{vendor.purchasedCount || 0}</td>
                      <td>{formatCurrency(vendor.purchasedCost)}</td>
                      <td>{vendor.soldCount || 0}</td>
                      <td>{formatCurrency(vendor.soldRevenue)}</td>
                      <td>{vendor.returnCount || 0}</td>
                      <td>{formatCurrency(vendor.refundAmount)}</td>
                      <td>{vendor.openCount || 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8}>No vendor activity in this date range</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>

          <section className="parts-print-section">
            <h2>Part Activity</h2>
            <table>
              <thead>
                <tr>
                  <th>Part</th>
                  <th>Part #</th>
                  <th>Vendor</th>
                  <th>Status</th>
                  <th>Qty</th>
                  <th>Buy</th>
                  <th>Sell</th>
                  <th>Purchased</th>
                  <th>Resolved</th>
                  <th>Invoice</th>
                  <th>Customer</th>
                  <th>Refund</th>
                </tr>
              </thead>
              <tbody>
                {report.parts.length > 0 ? (
                  report.parts.map((part) => (
                    <tr key={part.id}>
                      <td>{part.name || "-"}</td>
                      <td>{part.partNumber || "-"}</td>
                      <td>{part.vendor || "-"}</td>
                      <td>{part.status || "-"}</td>
                      <td>{part.quantity || 0}</td>
                      <td>{formatCurrency(part.buyPrice)}</td>
                      <td>{formatCurrency(part.sellPrice)}</td>
                      <td>{formatDate(part.purchaseDate)}</td>
                      <td>{formatDate(part.resolvedAt)}</td>
                      <td>{part.linkedInvoice || "-"}</td>
                      <td>{part.customer || "-"}</td>
                      <td>{formatCurrency(part.refundAmount)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={12}>No special-order parts found in this date range</td>
                  </tr>
                )}
              </tbody>
            </table>
          </section>
        </div>
      </Box>
      <style>{`
        .print-only {
          display: none;
        }

        @media print {
          body {
            margin: 0;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          .screen-only {
            display: none !important;
          }

          .print-only {
            display: block !important;
            position: static !important;
            width: 100% !important;
            padding: 0 !important;
            margin: 0 !important;
          }

          @page {
            size: landscape;
            margin: 10mm;
          }

          .parts-print-report {
            color: #111827;
            font-family: Arial, sans-serif;
            font-size: 10px;
            line-height: 1.3;
          }

          .parts-print-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #0f766e;
            padding-bottom: 8px;
            margin-bottom: 10px;
          }

          .parts-print-header h1 {
            margin: 0;
            font-size: 20px;
          }

          .parts-print-header p,
          .parts-print-meta div {
            margin: 2px 0;
            color: #374151;
          }

          .parts-print-meta {
            text-align: right;
          }

          .parts-print-summary {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            margin-bottom: 12px;
          }

          .parts-print-summary div {
            border: 1px solid #d1d5db;
            border-radius: 4px;
            padding: 6px;
          }

          .parts-print-summary span,
          .parts-print-summary strong,
          .parts-print-summary em {
            display: block;
          }

          .parts-print-summary span {
            color: #4b5563;
            font-size: 9px;
            text-transform: uppercase;
          }

          .parts-print-summary strong {
            margin-top: 2px;
            font-size: 12px;
          }

          .parts-print-summary em {
            margin-top: 1px;
            font-style: normal;
            color: #0f766e;
            font-weight: 700;
          }

          .parts-print-section {
            margin-top: 12px;
            page-break-inside: avoid;
          }

          .parts-print-section h2 {
            margin: 0 0 6px;
            font-size: 13px;
          }

          .parts-print-section table {
            width: 100%;
            border-collapse: collapse;
          }

          .parts-print-section th,
          .parts-print-section td {
            border: 1px solid #d1d5db;
            padding: 4px;
            vertical-align: top;
          }

          .parts-print-section th {
            background: #eef2f7;
            font-weight: 700;
            text-align: left;
          }

          .parts-print-section tr {
            page-break-inside: avoid;
          }
        }
      `}</style>
    </>
  );
}

export default PartReports;
